import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { ERROR_CODE_MAP } from '../utils/error-code-map';
import { environment } from 'src/environments/environment';

/* -------------------------------------------------------------------------- */
/*                             Interface Definitions                          */
/* -------------------------------------------------------------------------- */

/**
 * Request payload structure for creating a new payment method.
 * Typically contains a tokenized representation of a card or bank account.
 *
 * @property type - The payment method type
 * @property token_id - The vault token ID
 * @property token_data - The masked token metadata
 * @property save_payment_method - Indicates if the payment method should be saved
 */
export interface PaymentMethodRequest {
  type: 'card' | 'ach'; // Specifies the payment method type
  token_id: string; // Token reference from a secure vault
  token_data: any; // Masked token metadata (PCI-compliant)
  save_payment_method: boolean;
}

/**
 * Response structure for a stored payment method.
 * Returned when listing, creating, or retrieving payment methods.
 *
 * @property payment_method_code - The unique internal identifier for the method
 * @property customer_id - The linked customer ID
 * @property type - The payment method type
 * @property token_id - The vault token ID
 * @property token_data - The masked token metadata
 * @property is_default - Indicates if it's the default method
 */
export interface PaymentMethodResponse {
  payment_method_code: string; // Unique internal identifier for the method
  customer_id: string; // Linked customer ID
  type: string; // 'card' | 'ach'
  token_id: string; // Vault token ID
  token_data: any; // Masked token metadata
  is_default: boolean; // Indicates if it's the default method
  created_at: string;
  modified_at: string;
  deleted_at?: string; // Present only if soft-deleted
  created_by: string;
  modified_by: string;
}

/**
 * Request structure for a payment transaction.
 *
 * @property amount - The amount to be processed
 * @property customer_id - The customer ID
 * @property office_id - The office ID
 * @property payment_method - The payment method to be used
 */
export interface ProcessPaymentRequest {
  amount: number;
  customer_id: string;
  office_id: string;
  payment_method: Partial<PaymentMethodRequest>;
  source_type: string;
  source_ids: string[];
}

/**
 * Response structure for a payment transaction.
 *
 * @property amount - The amount to be processed
 * @property created_at - The date and time the payment was created
 * @property created_by - The user who created the payment
 * @property deleted_at - The date and time the payment was deleted
 * @property modifed_at - The date and time the payment was modified
 * @property modifed_by - The user who modified the payment
 */
export interface ProcessPaymentResponse {
  amount: number;
  created_at: string;
  created_by: string;
  deleted_at?: string;
  modifed_at: string;
  modifed_by: string;
  payment_code: string;
  status: string; // e.g., "Success", "Failed"
}

export interface OrganizationResponse extends Auditable {
  id: number; // changed from string to number
  name: string;
  organization_id: string;
  knostos_company_id: string;
  bridgepay_pci_api_key: string;
}

export interface Auditable {
  created_at: string;
  modified_at: string;
  deleted_at?: string;
  created_by: string;
  modified_by: string;
  deleted_by?: string;
}
export interface SuccessResponse<T> {
  data: T;
  message: string;
  tracking_id: string;
}

export interface ErrorResponse {
  error: AppError;
  tracking_id: string;
}

export interface AppError {
  code: string;
  details: Record<string, any>;
  message: string;
}

/**
 * Standard success response structure wrapping actual data payload.
 *
 * @property data - The actual data payload
 * @property message - A message describing the success
 */
export interface SuccessResponse<T = any> {
  data: T;
  message: string;
}

/**
 * Standard error response returned by the API on failure.
 *
 * @property code - Unique error code for debugging
 * @property message - Human-readable message
 * @property details - Optional structured error context
 */
export interface ErrorResponse {
  code: string; // Unique error code for debugging
  message: string; // Human-readable message
  details?: any; // Optional structured error context
}

/* -------------------------------------------------------------------------- */
/*                             Payment Service API                            */
/* -------------------------------------------------------------------------- */

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  /**
   * Base URL for all payment-related endpoints.
   * Replace with environment-specific variable in production builds.
   */
  private readonly baseUrl = environment.apiUrl;
  private headers = new HttpHeaders({
    'app-id': 'nlSwUjIBL3QpB8++8OWCog==',
  });
  constructor(private http: HttpClient) {}

  /**
   * Retrieves all active (and optionally archived) payment methods
   * associated with a specific customer ID under the given tenant.
   *
   * @param company_uuid - Unique identifier for the organization or merchant
   * @param customerUuid - Unique customer or contact identifier
   * @returns Observable that emits an array of stored payment methods
   *
   * Example use case:
   * - Displaying saved cards/bank accounts for reuse during checkout.
   */
  getPaymentMethods(
    company_uuid: string,
    customerUuid: string
  ): Observable<SuccessResponse<PaymentMethodResponse[]>> {
    const url = `${this.baseUrl}/${company_uuid}/PaymentMethod/${customerUuid}`;
    return this.http.get<SuccessResponse<PaymentMethodResponse[]>>(url, {
      headers: this.headers,
      withCredentials: true,
    });
  }

  /**
   * Registers a new payment method (card or ACH) for a customer by
   * securely tokenizing and storing the method for future transactions.
   *
   * This typically occurs after tokenizing via a PCI-compliant vault provider
   * such as Stripe, Basis Theory, or a payment gateway.
   *
   * @param company_uuid - The tenant or merchant who owns the customer
   * @param customerUuid - The end user/customer being linked
   * @param payload - The tokenized and masked payment method details
   * @returns Observable that emits the saved payment method metadata
   *
   * Best practices:
   * - Ensure tokens are created client-side using PCI DSS-compliant flows.
   * - Validate method metadata (e.g. last4, brand) before saving.
   */
  createPaymentMethod(
    company_uuid: string,
    customerUuid: string,
    payload: PaymentMethodRequest
  ): Observable<SuccessResponse<PaymentMethodResponse>> {
    const url = `${this.baseUrl}/${company_uuid}/PaymentMethod/${customerUuid}`;
    return this.http.post<SuccessResponse<PaymentMethodResponse>>(url, payload);
  }

  /**
   * Initiates a payment transaction using a saved payment method.
   *
   * @param company_uuid - Company or tenant identifier
   * @param payload - Payment execution details including amount, customer, etc.
   * @returns Observable with payment result
   */
  processPayment(
    company_uuid: string,
    payload: ProcessPaymentRequest
  ): Observable<SuccessResponse<ProcessPaymentResponse>> {
    const url = `${this.baseUrl}/${company_uuid}/Payment`;
    return this.http.post<SuccessResponse<ProcessPaymentResponse>>(
      url,
      payload,
      {
        headers: this.headers,
        withCredentials: true,
      }
    );
  }

  getDefaultOrganization(
    companyId: string
  ): Observable<SuccessResponse<OrganizationResponse>> {
    return this.http
      .get<SuccessResponse<OrganizationResponse>>(
        `${this.baseUrl}/${companyId}/Organization/default`,
        { headers: this.headers, withCredentials: true }
      )
      .pipe(catchError((error) => this.handleError(error)));
  }

  //  getSavedPaymentMethods(companyId: string, contactUuid:string): Observable<SuccessResponse<PaymentMethodResponse>> {
  //   return this.http.get<SuccessResponse<PaymentMethodResponse>>(`${this.baseUrl}/${companyId}/PaymentMethod/${contactUuid}`, {
  //   }).pipe(
  //     catchError((error) => this.handleError(error))
  //   );
  // }

  private handleError(error: any): Observable<never> {
    if (error.error?.error) {
      const apiError: ErrorResponse = error.error;
      const userMessage =
        ERROR_CODE_MAP[apiError.error.code] || 'An unexpected error occurred.';

      return throwError(() => ({
        ...apiError,
        userMessage, // Attach user-friendly message
      }));
    }

    return throwError(() => ({
      error: {
        code: 'UNEXPECTED_ERROR',
        details: {},
        message: 'An unexpected error occurred.',
      },
      tracking_id: error.tracking_id || 'N/A',
    }));
  }

  updateInvoicesStatus(
    ids: string[],
    company_uuid: string
  ): Observable<SuccessResponse<any>> {
    const url = `${this.baseUrl}/${company_uuid}/Payment/sources/ACTIVITY/`;
    const body = {
      source_ids: ids,
    };
    return this.http
      .post<SuccessResponse<any>>(url, body)
      .pipe(catchError((error) => this.handleError(error)));
  }
}
